/* eslint-disable no-param-reassign */
import React, {
    useEffect,
    useReducer,
} from 'react';
import {
    Grid,
    FormLabel,
    makeStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import ArrayUtils from 'lib/ArrayUtils';
import update from 'immutability-helper';
import Table from 'components/widgets/Table';
import Filter from 'components/widgets/Filter';
import Loading from 'components/widgets/Loading';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';

const useStyles = makeStyles((theme) => AccountingStyles.mainList(theme));

const ownStyles = makeStyles(() => ({
    itemsCounter: {
        marginLeft: 15,
        marginRight: 15,
        alignSelf: 'center',
        width: 'fit-content',
        height: 'fit-content',
    },
    filterContainer: {
        height: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        '& > div': {
            position: 'absolute',
            left: '5px',
            width: '15%',
            textAlign: 'left',
            display: 'flex',
            paddingTop: '4px',
            paddingBottom: '4px',
        },
    },
    localButtonSpacing: {
        marginLeft: '8px',
    },
    titleLabel: {
        marginLeft: '8px',
        fontWeight: 'bold',
    },
    tableHeight: {
        height: 'calc(50vh - 170px)',
    },
    totalLabel: {
        marginLeft: 50,
    },
    grandTotalLabel: {
        marginLeft: 189,
    },
}));

const ACTION_TYPES = {
    APPLY_FILTER: 'applyFilter',
    SET_STATE_VALUE: 'setStateValue',
    SET_STATE_VALUES: 'setStateValues',
};

const reducer = (state, action) => {
    const { value } = action;
    switch (action.type) {
    case ACTION_TYPES.SET_STATE_VALUE:
        return update(state, {
            [action.field]: { $set: value },
        });
    case ACTION_TYPES.SET_STATE_VALUES: {
        return { ...state, ...action.values };
    }
    case ACTION_TYPES.APPLY_FILTER:
        const { filtersApplied } = state;
        const filtered = [...filtersApplied];
        let insertNewFilter = 1;

        if (filtered.length) {
            filtered.forEach((filter, i) => {
                if (filter.id === action.accessor) {
                    if (value === '' || !value.length) filtered.splice(i, 1);
                    else filter.value = value;
                    insertNewFilter = 0;
                }
            });
        }

        if (insertNewFilter && value.length) {
            filtered.push({ id: action.accessor, value });
        }
        return update(state, {
            filtersApplied: { $set: filtered },
        });
    default: return state;
    }
};

const ActualHoursTable = ({
    loadTable, items, regular, overtime, total,
}) => {
    const classes = {
        ...useStyles(),
        ...ownStyles(),
    };

    const initState = {
        filtersApplied: [],
    };

    const [state, dispatch] = useReducer(reducer, initState);

    const { filtersApplied } = state;

    useEffect(() => {
        dispatch({
            value: [],
            field: 'filtersApplied',
            type: ACTION_TYPES.SET_STATE_VALUE,
        });
    }, [items]);

    const getColumns = () => [{
        Header: 'Date',
        id: 'date',
        minWidth: 12,
        accessor: 'date',
        className: 'd-flex-justify-center-align-center',
    }, {
        Header: 'Date Of Week',
        id: 'dayOfWeek',
        minWidth: 12,
        accessor: 'dayOfWeek',
        className: 'd-flex-justify-center-align-center',
    }, {
        Header: 'Time In',
        id: 'timeIn',
        minWidth: 12,
        accessor: 'timeIn',
        className: 'd-flex-justify-center-align-center',
    }, {
        Header: 'Time In Reason',
        id: 'timeInReason',
        minWidth: 20,
        accessor: 'timeInReason',
        className: 'd-flex-justify-center-align-center',
    }, {
        Header: 'Time Out',
        id: 'timeOut',
        minWidth: 12,
        accessor: 'timeOut',
        className: 'd-flex-justify-center-align-center',
    }, {
        Header: 'Time Out Reason',
        id: 'timeOutReason',
        minWidth: 20,
        accessor: 'timeOutReason',
        className: 'd-flex-justify-center-align-center',
    }, {
        Header: 'Total Hours',
        id: 'totalHours',
        minWidth: 12,
        accessor: 'totalHours',
        className: 'd-flex-justify-center-align-center',
    }];

    const getFilterValues = (values, columnId) => {
        if (values?.length > 0) {
            const mappedRecords = [
                ...new Map(values
                    .map((a) => ({ value: a[columnId], label: (String(a[columnId]) || '(Blanks)') }))
                    .map((item) => [item.label, item])).values(),
            ];
            const sortedResult = ArrayUtils.sortByObjectField(mappedRecords, 'value');
            return sortedResult;
        }
        return [];
    };

    const onFilteredChangeCustom = (value, accessor) => {
        dispatch({
            value,
            accessor,
            type: ACTION_TYPES.APPLY_FILTER,
        });
    };

    const resetFilters = (columnId) => {
        onFilteredChangeCustom([], columnId);
    };

    const loadColumns = () => {
        const columns = getColumns();
        columns.forEach((column) => {
            column.Header = (
                <div
                    className={classes.filterContainer}
                >
                    {column.Header}
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <Filter
                            showTooltip
                            showIconOnly
                            useInternalSearch
                            maxWidthLabel={200}
                            filterId={column.id}
                            records={getFilterValues(items, column.id)}
                            onClearFilter={() => resetFilters(column.id)}
                            applyFilter={(record) => onFilteredChangeCustom(
                                record.map((item) => (item.label === '(Blanks)' ? '' : item.value)), column.id,
                            )}
                        />
                    </div>
                </div>
            );
        });
        return columns;
    };

    return (
        <>
            <FormLabel className={classes.titleLabel}>
                Clocked Hours:
            </FormLabel>
            <Grid item xs={12} className={classes.tableHeight}>
                {!loadTable && (
                    <div className={classes.loadingIndicator}>
                        <Loading />
                    </div>
                )}
                {loadTable && (
                    <Table
                        enableRowHover
                        className="-highlight payrollTable"
                        columns={loadColumns()}
                        filtered={filtersApplied}
                        totalRecords={items.length}
                        data={items}
                        defaultFilterMethod={(localFilter, row) => {
                            const id = localFilter.pivotId || localFilter.id;
                            if (typeof localFilter.value === 'object') {
                                return row[id] !== undefined
                                    ? localFilter.value.indexOf(row[id]) > -1
                                    : true;
                            }
                            return row[id] !== undefined
                                ? String(row[id]).indexOf(localFilter.value) > -1
                                : true;
                        }}
                    />
                )}
                <div>
                    <FormLabel className={classes.localButtonSpacing}>
                        {`Total Regular Hours: ${regular}`}
                    </FormLabel>
                    <FormLabel className={classes.totalLabel}>
                        {`Total Overtime: ${overtime}`}
                    </FormLabel>
                    <FormLabel className={classes.totalLabel}>
                        {`Total Hours: ${total}`}
                    </FormLabel>
                </div>
            </Grid>
        </>
    );
};

ActualHoursTable.propTypes = {
    items: PropTypes.array.isRequired,
    total: PropTypes.string.isRequired,
    loadTable: PropTypes.bool.isRequired,
    regular: PropTypes.string.isRequired,
    overtime: PropTypes.string.isRequired,
};

export default ActualHoursTable;
